import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (n ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const bjMonto = (agencia) =>
  (Math.round((agencia.blackjack_resultado || 0) * (agencia.blackjack_porcentaje || 0)) / 100) || 0;

// Blackjack de la agencia: resultado (apuestas + pagos), % que le comparte el club
// (default 0) y check para incluirlo en el cierre que se le envía.
// Total GAN + RBK con el blackjack ya sumado cuando está incluido en el cierre.
export const GanRbkConBj = ({ ganRbk, bj }) => (
  <div className="flex flex-col items-end">
    <span className={`font-bold text-lg ${ganRbk < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
      {fmt(ganRbk)}
    </span>
    {!!bj && (
      <span className="text-[10px] text-amber-600 dark:text-amber-400 whitespace-nowrap">
        incl. BJ {fmt(bj)}
      </span>
    )}
  </div>
);

export const AgenciaBlackjackCell = ({ agencia, incluido, onToggle, onSaved }) => {
  const [editando, setEditando] = useState(false);
  const [pct, setPct] = useState(agencia.blackjack_porcentaje || 0);
  const res = agencia.blackjack_resultado || 0;
  const monto = bjMonto(agencia);

  // El check queda guardado por agencia: se aplica solo en los cierres siguientes.
  const toggleIncluir = async (valor) => {
    onToggle(valor);
    try {
      await axios.put(`${API}/poker/agencias/${agencia.agencia_id}/blackjack-incluir`,
        { incluir: valor });
      toast.success(valor
        ? `Blackjack queda incluido en los cierres de ${agencia.agencia_name}`
        : `Blackjack ya no se incluye en los cierres de ${agencia.agencia_name}`);
    } catch (e) {
      onToggle(!valor);
      toast.error('No se pudo guardar la preferencia de blackjack');
    }
  };

  const guardar = async () => {
    try {
      await axios.put(`${API}/poker/agencias/${agencia.agencia_id}/blackjack-porcentaje`,
        { porcentaje: Number(pct) });
      setEditando(false);
      toast.success(`Blackjack ${pct}% para ${agencia.agencia_name}`);
      onSaved?.();
    } catch (e) {
      toast.error('No se pudo guardar el % de blackjack');
    }
  };

  if (!res && !agencia.bj_apuestas) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  return (
    <div className="inline-flex flex-col items-end gap-0.5 whitespace-nowrap" data-testid={`ag-bj-${agencia.agencia_id}`}
      title={`Apuestas ${fmt(agencia.bj_apuestas)} + Pagos ${fmt(agencia.bj_pagos)} = Resultado ${fmt(res)}`}>
      <span className={`text-sm font-semibold ${res < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
        {fmt(res)}
      </span>
      <div className="flex items-center gap-1">
        {editando ? (
          <>
            <input type="number" min="0" max="100" value={pct} autoFocus
              onChange={(e) => setPct(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') setEditando(false); }}
              className="w-14 px-1 py-0.5 text-xs border-2 border-amber-500 rounded text-right dark:bg-gray-800"
              data-testid={`ag-bj-input-${agencia.agencia_id}`} />
            <button onClick={guardar} className="text-emerald-600 hover:text-emerald-800"
              data-testid={`ag-bj-save-${agencia.agencia_id}`}>
              <Check className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <button onClick={() => { setPct(agencia.blackjack_porcentaje || 0); setEditando(true); }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-200 group"
            title="% del resultado que el club le comparte a esta agencia"
            data-testid={`ag-bj-pct-${agencia.agencia_id}`}>
            {agencia.blackjack_porcentaje || 0}% = {fmt(monto)}
            <Edit2 className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
          </button>
        )}
      </div>
      <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 cursor-pointer select-none whitespace-nowrap">
        <input type="checkbox" checked={!!incluido} onChange={(e) => toggleIncluir(e.target.checked)}
          className="w-3 h-3 accent-emerald-600"
          data-testid={`ag-bj-check-${agencia.agencia_id}`} />
        incluir
      </label>
    </div>
  );
};
